import React from 'react';

import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';
import type { ShiftDayCellViewModel, AssignedScheduleView } from '@/hooks/shift/useShiftWeeklyDistribution';


interface ShiftDayCardProps {
  day: ShiftDayCellViewModel;
  onClick: (daySequence: number) => void;
}

const RestDayIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke={colors.feedback.error300} strokeWidth="1.5" />
    <line x1="6" y1="18" x2="18" y2="6" stroke={colors.feedback.error300} strokeWidth="1.5" />
  </svg>
);

const formatNetHours = (hours: number): string => (
  Number.isInteger(hours) ? String(hours) : hours.toFixed(1)
);

const ScheduleBlock: React.FC<{ schedule: AssignedScheduleView; accentColor: string }> = ({
  schedule,
  accentColor,
}) => {
  const { t: translate } = useTranslation('schedule');

  return (
    <div className="flex gap-2 border-b pb-1 mb-1" style={{ borderColor: colors.grays.neutralEE }}>
      <div style={{ width: 3, backgroundColor: accentColor, borderRadius: 2 }} />
      <div className="flex flex-col">
        <Text variant="small-regular" color={colors.grays.neutral33}>
          {schedule.name}
        </Text>
        <Text variant="small-regular" color={colors.feedback.alert300}>
          {schedule.startTime} - {schedule.endTime}
        </Text>
        <Text variant="small-regular" color={colors.important.main}>
          {translate('shift.create.fields.workHours', { hours: formatNetHours(schedule.netHours) })}
        </Text>
        <Text variant="small-regular" color={colors.important.main}>
          {translate('shift.create.fields.breakMinutesShort', { minutes: schedule.breakMinutes })}
        </Text>
      </div>
    </div>
  );
};

export const ShiftDayCard: React.FC<ShiftDayCardProps> = ({ day, onClick }) => {
  const { t: translate } = useTranslation('schedule');

  const dayLabel = day.dayLabelKey ? translate(`shift.create.fields.days.${day.dayLabelKey}`) : null;
  const hasAssignment = day.schedule1 !== null;

  if (day.isOffPattern) {
    return (
      <div
        className="rounded-lg p-4 flex flex-col items-center justify-center gap-2"
        style={{ backgroundColor: colors.grays.neutralFA, minHeight: 140 }}
        data-testid={`shift-day-card-${day.daySequence}`}
      >
        <div className="flex justify-between w-full">
          {dayLabel && <Text variant="small-medium" color={colors.grays.neutral66}>{dayLabel}</Text>}
          <Text variant="small-medium" color={colors.primary.dash}>{day.dayNumberLabel}</Text>
        </div>
        <RestDayIcon />
        <Text variant="small-regular" color={colors.grays.neutral66}>
          {translate('shift.create.fields.restDay')}
        </Text>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onClick(day.daySequence)}
      className="rounded-lg p-4 flex flex-col text-left"
      style={{ backgroundColor: colors.grays.neutralFA, minHeight: 140, cursor: 'pointer' }}
      data-testid={`shift-day-card-${day.daySequence}`}
    >
      <div className="flex justify-between w-full">
        {dayLabel && <Text variant="small-medium" color={colors.grays.neutral66}>{dayLabel}</Text>}
        <Text variant="small-medium" color={colors.primary.dash}>{day.dayNumberLabel}</Text>
      </div>

      {hasAssignment ? (
        <div className="mt-2">
          {day.schedule1 && (
            <ScheduleBlock schedule={day.schedule1} accentColor={colors.important.main} />
          )}
          {day.schedule2 && (
            <ScheduleBlock schedule={day.schedule2} accentColor={colors.feedback.success300} />
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-1 mt-2">
          <Text variant="small-medium" color={colors.important.main}>+</Text>
          <Text variant="small-regular" color={colors.important.main}>
            {translate('shift.create.fields.assignSchedule')}
          </Text>
        </div>
      )}
    </button>
  );
};

export default ShiftDayCard;
